import React from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import IncidentDrawer from '../../components/incidents/IncidentDrawer';
import IncidentSuppressDialog from '../../components/incidents/IncidentSuppressDialog';
import { GroupedReviewStatusRow } from '../../components/incidents/GroupedIncidentReview';
import {
  ObservationDispositionHeader,
  ParentPatternCaseBanner,
} from '../../components/incidents/ObservationDispositionChrome';
import { GROUPED_REVIEW_WORKSPACE_HINT } from '../../domain/incidents/incidentPatternVocabulary';
import type { ReviewWindowContext } from '../../domain/incidents/reviewWindowContext';
import {
  isObservationDispositionMode,
  isPatternCaseReviewMode,
  isStandaloneIncidentReviewMode,
  type IncidentReviewSurfaceMode,
} from '../../domain/incidents/incidentReviewSurfaceMode';
import { OBSERVATION_DISPOSITION_BACK_LABEL, OBSERVATION_DISPOSITION_PAGE_OVERLINE } from '../../domain/incidents/observationDispositionCopy';
import { RISK_PATTERN_EVIDENCE_REVIEW_HEADING } from '../../domain/platform/platformOrientationModel';
import { PlatformWorkflowStrip } from '../../components/platform/PlatformWorkflowStrip';
import type { Incident, IncidentGroup } from '../../services/incidentsApi';
import { incidentDisplaySummaryForRow, incidentDisplayTitleForRow } from './incidentDisplayModel';
import { incidentTypeLabel } from './incidentTypeLabels';
import { INCIDENT_REVIEW_PHASES, reviewSeriesTimeLabel } from './incidentsReviewPhases';
import { deriveReviewerCaseState } from '../../domain/incidents/reviewerCaseState';

export type IncidentsReviewPageProps = {
  reviewWorkspaceTitle: string;
  reviewPatternLabel?: string;
  reviewObservationLocationLine?: string;
  reviewWorkspaceId: string;
  reviewSurfaceMode: IncidentReviewSurfaceMode;
  reviewRelatedIncidentCount: number;
  reviewPatternVolumeCount: number;
  observationParentCaseCount: number;
  reviewPatternVolumeKind?: 'persisted_episode' | 'review_window' | 'single';
  reviewWindowContext: ReviewWindowContext;
  reviewGroupedRecurrenceLabel: string;
  reviewGroupedWorkflowLabel: string;
  reviewSeriesActionLabel: string;
  reviewSeriesIncidents: Incident[];
  reviewSeriesHiddenCount: number;
  workflowProblemContext?: { place: string; problem: string } | null;
  selectedIncident: Incident | null;
  selectedGroupContext: IncidentGroup | null;
  error: string | null;
  onClearError: () => void;
  canOpenArtifactEntry: boolean;
  onExitReview: () => void;
  onOpenProofPackage: () => void;
  onOpenReviewSeriesQueue: () => void;
  onOpenIncidentReviewPage: (incidentId: string, options?: { exact?: boolean }) => void;
  incidentDrawerCtx: Record<string, unknown>;
  incidentDialogs: React.ReactNode;
  suppressDialogOpen: boolean;
  onCloseSuppressDialog: () => void;
  drawerBusy: string | null;
  drawerIncidentLoading: boolean;
  canManageIncidentSuppressions: boolean;
  onSuppressSimilar: (durationSec: number, reason: string) => Promise<void>;
  severityIcon: (severity: string) => React.ReactNode;
  severityChipSx: (severity: string) => SxProps<Theme>;
  opsStateLabel: (value: unknown) => string;
};

export const IncidentsReviewPage: React.FC<IncidentsReviewPageProps> = ({
  reviewWorkspaceTitle,
  reviewPatternLabel = '',
  reviewObservationLocationLine = '',
  reviewWorkspaceId,
  reviewSurfaceMode,
  reviewRelatedIncidentCount,
  reviewPatternVolumeCount,
  observationParentCaseCount,
  reviewPatternVolumeKind = 'review_window',
  reviewWindowContext,
  reviewGroupedRecurrenceLabel,
  reviewGroupedWorkflowLabel,
  reviewSeriesActionLabel,
  reviewSeriesIncidents,
  reviewSeriesHiddenCount,
  workflowProblemContext = null,
  selectedIncident,
  selectedGroupContext,
  error,
  onClearError,
  canOpenArtifactEntry,
  onExitReview,
  onOpenProofPackage,
  onOpenReviewSeriesQueue,
  onOpenIncidentReviewPage,
  incidentDrawerCtx,
  incidentDialogs,
  suppressDialogOpen,
  onCloseSuppressDialog,
  drawerBusy,
  drawerIncidentLoading,
  canManageIncidentSuppressions,
  onSuppressSimilar,
  severityIcon,
  severityChipSx,
  opsStateLabel,
}) => {
  const patternCaseReviewMode = isPatternCaseReviewMode(reviewSurfaceMode);
  const observationDispositionMode = isObservationDispositionMode(reviewSurfaceMode);
  const standaloneIncidentMode = isStandaloneIncidentReviewMode(reviewSurfaceMode);
  const reviewerCaseState = patternCaseReviewMode
    ? deriveReviewerCaseState((selectedGroupContext as IncidentGroup | null) || (selectedIncident as Incident | null) || {})
    : null;
  const groupedReviewAlertSeverity =
    reviewerCaseState?.tone === 'warning'
      ? 'warning'
      : reviewerCaseState?.tone === 'success'
        ? 'success'
        : 'info';
  const parentCaseIncidentId = String(
    (selectedGroupContext as any)?.review_anchor_incident_id
    || (selectedGroupContext as any)?.linked_detailed_record_id
    || (selectedGroupContext as any)?.incident_id
    || '',
  ).trim();
  const observationHeaderLocation = reviewObservationLocationLine || (selectedIncident
    ? incidentDisplaySummaryForRow(selectedIncident as any, { includeLocation: true })
    : '');
  const observationHeaderTimestamp = reviewSeriesTimeLabel(
    selectedIncident?.last_edge_update_at
    || selectedIncident?.updated_at
    || selectedIncident?.created_at
    || selectedIncident?.ts_start,
  );

  return (
    <Box sx={{ pb: 3 }}>
    <PlatformWorkflowStrip currentStep="review" secondaryStep="action" compact />

    <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
      <Box>
        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
          {observationDispositionMode ? OBSERVATION_DISPOSITION_PAGE_OVERLINE : RISK_PATTERN_EVIDENCE_REVIEW_HEADING}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          {reviewWorkspaceTitle || (selectedIncident?.incident_id ? 'Review workspace' : 'Loading review workspace')}
        </Typography>
        {workflowProblemContext && (workflowProblemContext.place || workflowProblemContext.problem) ? (
          <Box data-testid="incident-workflow-problem-context" sx={{ mt: 0.75 }}>
            {workflowProblemContext.place ? (
              <Typography variant="body1" sx={{ fontWeight: 700 }} data-testid="incident-workflow-problem-place">
                {workflowProblemContext.place}
              </Typography>
            ) : null}
            {workflowProblemContext.problem ? (
              <Typography variant="body2" color="text.secondary" data-testid="incident-workflow-problem-name">
                {workflowProblemContext.problem}
              </Typography>
            ) : null}
          </Box>
        ) : null}
        {observationDispositionMode ? (
          <ObservationDispositionHeader
            locationLine={observationHeaderLocation}
            incidentId={reviewWorkspaceId}
            observedAtLabel={observationHeaderTimestamp}
          />
        ) : null}
        {patternCaseReviewMode ? (
          <>
            <GroupedReviewStatusRow
              patternVolumeCount={reviewPatternVolumeCount}
              patternVolumeKind={reviewPatternVolumeKind}
              reviewWindowContext={reviewWindowContext}
              recurrenceLabel={reviewGroupedRecurrenceLabel}
              workflowLabel={reviewGroupedWorkflowLabel}
              onViewRelatedIncidents={reviewRelatedIncidentCount > 1 ? onOpenReviewSeriesQueue : undefined}
              viewRelatedLabel={reviewSeriesActionLabel}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.55, maxWidth: 720 }}>
              {GROUPED_REVIEW_WORKSPACE_HINT}
            </Typography>
          </>
        ) : standaloneIncidentMode ? (
          <Typography variant="body2" color="text.secondary">
            {reviewWorkspaceId
              ? `Review recurring risk patterns, inspect evidence, assign corrective action, and track follow-up in this workspace.`
              : 'Use the full-page review workspace to inspect recurring risk patterns, assign corrective action, and track follow-up.'}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
        <Button variant="outlined" onClick={onExitReview}>
          {observationDispositionMode
            ? OBSERVATION_DISPOSITION_BACK_LABEL
            : patternCaseReviewMode
              ? 'Back to patterns'
              : 'Back to incidents'}
        </Button>
        {standaloneIncidentMode && canOpenArtifactEntry ? (
          <Button variant="contained" onClick={onOpenProofPackage}>
            Safety Report
          </Button>
        ) : null}
        {standaloneIncidentMode && reviewRelatedIncidentCount > 1 ? (
          <Button variant="text" onClick={onOpenReviewSeriesQueue}>
            {reviewSeriesActionLabel}
          </Button>
        ) : null}
      </Box>
    </Box>

    {error ? (
      <Alert severity="warning" sx={{ mb: 2 }} onClose={onClearError}>
        {error}
      </Alert>
    ) : null}

    {patternCaseReviewMode ? (
      reviewerCaseState ? (
        <Alert severity={groupedReviewAlertSeverity} sx={{ mb: 2, borderRadius: 2.5, minHeight: 108 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.25 }}>
            {reviewerCaseState.label}
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.55 }}>
            {reviewerCaseState.helper}
          </Typography>
          {(() => {
            const needsReviewWindow = /needs review/i.test(String(reviewGroupedWorkflowLabel || ''));
            // Do not claim "no action needed" when this page still shows Needs review / open work.
            if (reviewerCaseState.key === 'reviewer_engaged' && needsReviewWindow) {
              return (
                <Typography variant="body2" sx={{ lineHeight: 1.55, mt: 0.75, fontWeight: 700 }}>
                  This review window still needs attention. Use Next required action below.
                </Typography>
              );
            }
            return (
              <Typography variant="body2" sx={{ lineHeight: 1.55, mt: 0.75, fontWeight: 700 }}>
                {reviewerCaseState.consequence}
              </Typography>
            );
          })()}
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2.5, minHeight: 108 }} aria-busy="true">
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.25 }}>
            Loading case status…
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.55 }} color="text.secondary">
            Preparing the review status for this pattern.
          </Typography>
        </Alert>
      )
    ) : null}

    {observationDispositionMode ? (
      <ParentPatternCaseBanner
        observationCount={observationParentCaseCount}
        patternLabel={reviewPatternLabel || reviewWorkspaceTitle}
        parentCaseIncidentId={parentCaseIncidentId}
        onOpenParentCase={(incidentId) => onOpenIncidentReviewPage(incidentId, { exact: false })}
      />
    ) : null}

    {standaloneIncidentMode ? (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3, borderColor: '#dbe4f0', bgcolor: '#f8fbff' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
          Operator workflow
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          This page is intentionally structured around the five review phases so the operator journey is obvious before the detailed steps begin.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, minmax(0, 1fr))' }, gap: 1 }}>
          {INCIDENT_REVIEW_PHASES.map((phase, index) => (
            <Paper key={phase.title} variant="outlined" sx={{ p: 1.25, borderRadius: 2.5, borderColor: '#d6deeb', bgcolor: '#ffffff' }}>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 800, letterSpacing: '0.06em' }}>
                Phase {index + 1}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mt: 0.25, mb: 0.35 }}>
                {phase.title}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45, display: 'block' }}>
                {phase.detail}
              </Typography>
            </Paper>
          ))}
        </Box>
        <Box
          sx={{
            mt: 1.5,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: canOpenArtifactEntry ? 'minmax(0, 1.15fr) minmax(0, 0.85fr)' : '1fr' },
            gap: 1,
          }}
        >
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, borderColor: '#d6deeb', bgcolor: '#ffffff' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.4 }}>
              Stay in Review
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              Verify the incident, inspect media, assign an owner, record corrective action, and update follow-up here. Review is the operational workspace for working the case.
            </Typography>
          </Paper>
          {canOpenArtifactEntry ? (
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, borderColor: '#d6deeb', bgcolor: '#ffffff' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.4 }}>
                Safety Report
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Open Safety Report when a reviewed pattern needs evidence packaging, trust review, or signoff support. The report should summarize one scope derived from Review, not replace the operational workflow.
              </Typography>
              <Button size="small" variant="text" sx={{ mt: 1 }} onClick={onOpenProofPackage}>
                Safety Report
              </Button>
            </Paper>
          ) : null}
        </Box>
      </Paper>
    ) : null}

    {!patternCaseReviewMode && !observationDispositionMode && reviewRelatedIncidentCount > 1 ? (
      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 3, borderColor: '#dbe4f0' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.4 }}>
              Incident series
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedGroupContext
                ? `This review is anchored on one incident inside a ${reviewRelatedIncidentCount}-alert series. Open a sibling incident below without leaving the review workspace.`
                : `Related incidents from this same recurring risk are available here so you can compare the latest alert against earlier ones.`}
            </Typography>
          </Box>
          <Button size="small" variant="text" onClick={onOpenReviewSeriesQueue}>
            {reviewSeriesActionLabel}
          </Button>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1, mt: 1.5 }}>
          {reviewSeriesIncidents.map((incident) => {
            const incidentId = String(incident.incident_id || '').trim();
            const isCurrent = incidentId === reviewWorkspaceId;
            const subtitleParts = [
              reviewSeriesTimeLabel(incident.last_edge_update_at || incident.updated_at || incident.created_at || incident.ts_start),
              incident.camera_id ? `Camera ${incident.camera_id}` : '',
              incident.zone_id ? `Zone ${incident.zone_id}` : '',
            ].filter(Boolean);
            return (
              <Paper
                key={incidentId}
                variant="outlined"
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  borderColor: isCurrent ? 'primary.main' : '#d6deeb',
                  bgcolor: isCurrent ? '#eef5ff' : '#fff',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      {incidentDisplayTitleForRow(incident as any)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.2 }}>
                      {incidentId}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.45 }}>
                      {subtitleParts.join(' | ')}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    {...(() => {
                      const chipIcon = severityIcon(String(incident.severity || ''));
                      return chipIcon ? { icon: chipIcon as React.ReactElement } : {};
                    })()}
                    label={isCurrent ? 'Current' : opsStateLabel(incident.ops_state || incident.status || 'new')}
                    sx={isCurrent ? { bgcolor: 'primary.main', color: 'common.white', fontWeight: 800 } : severityChipSx(String(incident.severity || ''))}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1.1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {incident.policy_name || incidentTypeLabel(String(incident.incident_type || 'incident'))}
                  </Typography>
                  <Button
                    size="small"
                    variant={isCurrent ? 'outlined' : 'text'}
                    disabled={isCurrent}
                    onClick={() => onOpenIncidentReviewPage(incidentId, { exact: true })}
                  >
                    {isCurrent ? 'Current incident' : 'Open incident'}
                  </Button>
                </Box>
              </Paper>
            );
          })}
        </Box>
        {reviewSeriesHiddenCount > 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.2 }}>
            {`Showing ${reviewSeriesIncidents.length} incidents here. ${reviewSeriesHiddenCount} more remain in the full series queue.`}
          </Typography>
        ) : null}
      </Paper>
    ) : null}

    {selectedIncident?.incident_id || selectedGroupContext ? (
      <>
        {drawerIncidentLoading && !selectedIncident?.incident_id ? (
          <Alert severity="info" sx={{ mb: 1.5, py: 0.35 }}>
            Loading latest incident details…
          </Alert>
        ) : null}
        <IncidentDrawer ctx={incidentDrawerCtx} />
      </>
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 240, gap: 1 }}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          Loading incident details…
        </Typography>
      </Box>
    )}

    <IncidentSuppressDialog
      open={suppressDialogOpen}
      onClose={onCloseSuppressDialog}
      selectedIncident={selectedIncident}
      drawerBusy={drawerBusy}
      canManage={canManageIncidentSuppressions}
      incidentTypeLabel={incidentTypeLabel}
      onSuppress={async ({ durationSec, reason }) => {
        await onSuppressSimilar(durationSec, reason);
      }}
    />
    {incidentDialogs}
    </Box>
  );
};
